<template>
  <div id="pageDashboard">
    <v-container>
      <v-card class="ma-0">
        <v-card-text class="pt-0 pb-0">
          <v-row>
            <v-col cols="6">
              <v-autocomplete
                ref="autoCompleteInternalInput"
                clearable
                v-model="organizationFilter"
                item-text="text"
                item-value="id"
                :items="organizationList"
                :label="$t('Organizations')"
                @change="organizationFilterChanged"
                multiple
              ></v-autocomplete>
            </v-col>
            <v-col>
              <v-select
                v-model="sortBy"
                :items="sortByItems"
                item-value="id"
                item-text="text"
                :label="$t('SortBy')"
                @change="sortChanged"
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                v-model="sortType"
                :items="sortTypeItems"
                item-value="id"
                item-text="text"
                label
                @change="sortChanged"
              ></v-select>
            </v-col>
            <v-col align-self="center">
              <v-btn @click="newOrganization" color="primary">
                <v-icon>mdi-plus</v-icon>
                {{ $t("NewOrganization") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
    <v-tabs centered icons-and-text @change="tabChange" v-model="activeTab">
      <v-tab href="#tabOrganizations">
        {{ $t("Organizations") }}
        <v-icon>mdi-domain</v-icon>
      </v-tab>
      <v-tab href="#tabDocuments">
        {{ $t("Documents") }}
        <v-icon>mdi-file-document</v-icon>
      </v-tab>
      <v-tab-item value="tabOrganizations">
        <v-container grid-list-xl fluid>
          <v-layout row wrap align-center v-if="loadingOrganization">
            <v-flex xs12>
              <div class="text-xs-center">
                <v-progress-circular
                  :size="50"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
            </v-flex>
          </v-layout>
          <v-layout row wrap v-if="!loadingOrganization">
            <!-- mini statistic start -->
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-nas"
                :title="totalUsedSpaceDisplay"
                :sub-title="$t('StorageUse')"
                color="red darken-4"
              />
            </v-flex>
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-account-box"
                :title="totalUsers + ''"
                :sub-title="$t('Users')"
                color="blue-grey"
              />
            </v-flex>
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-account-multiple"
                :title="totalConcurrentUsers + ''"
                :sub-title="$t('ConcurrentUsers')"
                color="lime darken-3"
              />
            </v-flex>
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-account-star"
                :title="totalAutorecognitionUsers + ''"
                :sub-title="$t('AutorecognitionUsers')"
                color="purple darken-1"
              />
            </v-flex>
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-cellphone-lock"
                :title="total2FAUsers + ''"
                :sub-title="$t('TwoFactorAuthenticationUsers')"
                color="green"
              />
            </v-flex>
            <v-flex lg4 sm6 xs12>
              <MiniStatistic
                icon="mdi-cellphone-android"
                :title="totalDevices + ''"
                :sub-title="$t('RegisteredDevices')"
                color="teal"
              />
            </v-flex>
            <v-flex
              lg3
              sm12
              xs12
              v-for="org in filteredOrganizations"
              :key="org.id"
            >
              <MiniOrganization
                :organization="org"
                @on-edit="onEditOrganization"
                @on-remove="onRemoveOrganization"
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>
      <v-tab-item value="tabDocuments" class="pa-10">
        <v-layout wrap>
          <v-flex lg4 xs12>
            <v-radio-group
              v-model="documentsChart.periodicity"
              :mandatory="true"
              row
              @change="changePeriodicity"
            >
              <v-radio :label="$t('Year')" value="Y" color="purple"></v-radio>
              <v-radio :label="$t('Month')" value="M" color="pink"></v-radio>
              <v-radio :label="$t('Week')" value="W" color="success"></v-radio>
              <v-radio :label="$t('Day')" value="D" color="warning"></v-radio>
            </v-radio-group>
          </v-flex>
          <v-flex lg4 xs12>
            <v-radio-group v-model="documentsChart.type" row>
              <v-radio
                :label="$t('LineChart')"
                value="L"
                color="primary"
              ></v-radio>
              <v-radio
                :label="$t('BarChart')"
                value="B"
                color="success"
              ></v-radio>
            </v-radio-group>
          </v-flex>
          <v-flex lg4 xs12>
            <BaseDatePicker
              v-model="documentsChart.date"
              @change="refreshDocuments"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap align-center v-if="loadingDocuments">
          <v-flex xs12>
            <div class="text-xs-center">
              <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
              ></v-progress-circular>
            </div>
          </v-flex>
        </v-layout>
        <v-card v-if="!loadingDocuments">
          <v-card-text>
            <LineChart
              ref="documentChart"
              v-if="!loadingDocuments && documentsChart.type == 'L'"
              :chartdata="documentsChart.data"
              :options="documentsChart.options"
            />

            <BarChart
              ref="documentChart"
              v-if="!loadingDocuments && documentsChart.type == 'B'"
              :chartdata="documentsChart.data"
              :options="documentsChart.options"
            />
          </v-card-text>
        </v-card>
        <v-card>
          <v-card-title>
            {{ $t('Documents') }}
          </v-card-title>
          <v-card-text>
            <div v-for="(orgSummary) in summaries.documentsSummary" :key="orgSummary.OrganizationId">
              {{ getOrganizationName(orgSummary.OrganizationId) }}: {{ orgSummary.Count }}
            </div>
          </v-card-text>
        </v-card>
        <v-card>
          <v-card-title>
            {{ $t('SignedDocuments') }}
          </v-card-title>
          <v-card-text>
            <div v-for="(orgSummary) in summaries.signedDocumentsSummary" :key="orgSummary.OrganizationId">
              {{ getOrganizationName(orgSummary.OrganizationId) }}: {{ orgSummary.Count }}
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
    <OrganizationWizardModal
      :ref="organizationWizardModalKey"
      @on-save="onSaveOrganization"
    />
    <OrganizationModal
      :ref="organizationModalKey"
      @on-save="onSaveOrganization"
    />
  </div>
</template>
<script>
import MiniOrganization from '@/components/admin/MiniOrganization';
import MiniStatistic from '@/components/widgets/statistic/MiniStatistic';
import LineChart from '@/components/widgets/statistic/LineChart';
import BarChart from '@/components/widgets/statistic/BarChart';

import OrganizationService from '@/services/OrganizationService';
import DashboardService from '@/services/DashboardService';
import OrganizationDashboard from '@/ecm/models/admin/OrganizationDashboard';
import OrganizationWizardModal from '@/components/admin/OrganizationWizardModal';
import OrganizationModal from '@/components/admin/OrganizationModal';
import Utils from '@/ecm/common/Utils';

export default {
    components: {
        MiniOrganization,
        MiniStatistic,
        LineChart,
        BarChart,
        OrganizationWizardModal,
        OrganizationModal,
    },
    data: (vm) => ({
        currentLanguage: 'en',
        activeTab: 'tabOrganizations',
        languages: [
            { flag: 'us', language: 'en' },
            { flag: 'es', language: 'es' },
            { flag: 'fr', language: 'fr' },
            { flag: 'de', language: 'de' },
            { flag: 'pt', language: 'pt' },
            { flag: 'ro', language: 'ro' },
            { flag: 'it', language: 'it' },
        ],
        sortByItems: [
            { id: 'totalDocuments', text: vm.$t('Documents') },
            { id: 'totalSignedDocuments', text: vm.$t('SignedDocuments') },
            { id: 'totalContentTypes', text: vm.$t('ContentTypes') },
            { id: 'totalUsedSpace', text: vm.$t('StorageUse') },
            { id: 'totalUsers', text: vm.$t('Users') },
            { id: 'autorecognitionEnabled', text: vm.$t('AutorecognitionLicense') },
            { id: 'totalConcurrentUsers', text: vm.$t('ConcurrentUsers') },
            { id: 'total2FAUsers', text: vm.$t('TwoFactorAuthenticationUsers') },
            { id: 'totalDevices', text: vm.$t('RegisteredDevices') }
        ],
        sortTypeItems: [
            { id: '', text: 'Asc' },
            { id: '-', text: 'Desc' },
        ],
        sortBy: 'totalDocuments',
        sortType: '-',
        organizationDashboards: [],
        organizationList: [],
        organizationFilter: null,
        loadingOrganization: false,
        loadingDocuments: false,
        summaries: {},
        documentsChart: {
            type: 'L',
            periodicity: 'Y',
            date: Utils.date.now(),
            data: null,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                // userCallback: function(label, index, labels) {
                                //   // when the floored value is the same as the value we have a whole number
                                //   if (Math.floor(label) === label) {
                                //     return label;
                                //   }
                                // }
                            },
                        },
                    ],
                },
            },
        },
        organizationWizardModalKey: 'organizationWizardModal',
        organizationModalKey: 'organizationModal',
    }),
    computed: {
        organizationWizardInstance() {
            return this.$refs[this.organizationWizardModalKey];
        },
        organizationModalInstance() {
            return this.$refs[this.organizationModalKey];
        },
        totalDocuments() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalDocuments)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalSignedDocuments() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalSignedDocuments)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalContentTypes() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalContentTypes)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalUsedSpace() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalUsedSpace)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalUsedSpaceDisplay() {
            return this.totalUsedSpace.toFixed(2) + ' GB';
        },
        totalUsers() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalUsers)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalConcurrentUsers() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalConcurrentUsers)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalAutorecognitionUsers() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.autorecognitionEnabled)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        total2FAUsers() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.total2FAUsers)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        totalDevices() {
            return this.organizationDashboards.length > 0
                ? this.organizationDashboards
                    .map((item) => item.totalDevices)
                    .reduce((prev, next) => prev + next)
                : 0;
        },
        filteredOrganizations() {
            if (this.organizationFilter && this.organizationFilter.length > 0) {
                var organizationsIds = this.organizationFilter.join();
                return this.organizationDashboards.filter((x) =>
                    organizationsIds.includes(x.id)
                );
            } else {
                return this.organizationDashboards;
            }
        },
    },
    methods: {
        changePeriodicity() {
            this.previousDate = null;
            this.refreshDocuments();
        },
        organizationFilterChanged() {
            this.getAutoCompleteValueAndClean();
            if (this.activeTab === 'tabDocuments') {
                this.previousDate = null;
                this.refreshDocuments();
            }
        },
        getAutoCompleteValueAndClean() {
            const inputElement = this.$refs.autoCompleteInternalInput.$refs.input;
            inputElement.value = '';
            inputElement.dispatchEvent(new Event('input'));
        },
        getOrganizationName(organizationId) {
            const organization = this.organizationList.find(o => parseInt(o.id) === organizationId);
            return organization.text;
        },
        async loadAllOrganizations() {
            const data = await OrganizationService.getList();
            this.organizationDashboards = [];
            this.organizationList = data.filter((o) => o.id !== '1');

            await this.semaphore(10, this.organizationList, DashboardService.getOrganizationDashboard);
            this.sortChanged();
        },
        refreshDocuments() {
            let organizationsIds = '';

            // Date should not be calling OnChange when you click the picker without changing the date
            // This is a fix to prevent calling the backend if date == oldDate
            // Remove when DatePicker.vue does not call INPUT event. Or change how it emits OnChange
            if (this.previousDate === this.documentsChart.date)
                return;

            this.previousDate = this.documentsChart.date;

            if (this.organizationFilter && this.organizationFilter.length > 0) {
                organizationsIds = this.organizationFilter.join();
            }

            if (organizationsIds === '')
                return;

            this.loadingDocuments = true;

            const filter = {
                Periodicity: this.documentsChart.periodicity,
                Date: this.documentsChart.date,
                OrganizationIds: organizationsIds || '',
            };

            DashboardService.getDocumentsDashboard(filter).then((data) => {
                this.summaries = {
                    documentsSummary: data.DocumentsSummary,
                    signedDocumentsSummary: data.SignedDocumentsSummary,
                };

                this.documentsChart.data = {
                    labels: data.Documents.Labels,
                    datasets: [
                        {
                            label: this.$t('Documents'),
                            backgroundColor: '#4CAF50',
                            fill: false,
                            borderWidth: 5,
                            lineTension: 0.2,
                            data: data.Documents.Series,
                        },
                        {
                            label: this.$t('SignedDocuments'),
                            backgroundColor: '#FF9800',
                            fill: false,
                            borderWidth: 5,
                            lineTension: 0.2,
                            data: data.SignedDocuments.Series,
                        },
                    ],
                };
                this.loadingDocuments = false;
            });
        },
        tabChange(selectedTab) {
            if (selectedTab === 'tabDocuments') {
                this.previousDate = null;
                this.refreshDocuments();
            }
        },
        refreshDashboard() {
            this.loadAllOrganizations();
            this.refreshDocuments();
        },
        sortChanged() {
            this.organizationDashboards = this.organizationDashboards.sort(
                dynamicSort(this.sortType + this.sortBy)
            );
        },
        newOrganization() {
            this.organizationWizardInstance.show();
        },
        onEditOrganization(organizationId) {
            this.organizationModalInstance.edit(organizationId);
        },
        onSaveOrganization() {
            this.refreshDashboard();
        },
        onRemoveOrganization(organizationId) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    OrganizationService.delete(organizationId).then((result) => {
                        if (result) {
                            this.refreshDashboard();
                            Utils.toast.success(
                                this.$t('Organization') +
                                    ' ' +
                                    this.$t('DeletedSuccessfullyFem')
                            );
                        }
                    });
                    this.organizationModalInstance.hide();
                }
            });
        },
        async semaphore(maxConcurrent, organizationList, getDashboardFn) {
            let current = 0;
            const queue = [];
            const that = this;

            async function worker() {
                while (queue.length) {
                    if (current >= maxConcurrent) {
                        // Wait until one of the active promises finishes
                        await new Promise((resolve) => setTimeout(resolve, 50));
                        continue;
                    }

                    const org = queue.shift(); // Get next org from the queue
                    current++; // Increment active promises count

                    await getDashboardFn(org.id) // Call the dashboard service
                        .then((dashboard) => {
                            that.organizationDashboards.push(new OrganizationDashboard(dashboard));
                        })
                        .catch((error) => {
                            console.error(`Failed to fetch dashboard for org ${org.id}`, error);
                        })
                        .finally(() => {
                            current--; // Decrement active promises count
                        });
                }
            }

            // Fill the queue with organizations
            queue.push(...organizationList);

            // Start 'maxConcurrent' number of workers
            await Promise.all(Array.from({ length: maxConcurrent }, worker));
        }
    },
    mounted() {
        this.loadAllOrganizations();
        this.currentLanguage = this.$store.getters['account/user'].CultureCode;
    },
};

function dynamicSort(property, asc) {
    var sortOrder = 1;
    if (property[0] === '-') {
        sortOrder = -1;
        property = property.substr(1);
    }
    return function (a, b) {
        /* next line works with strings and numbers,
         * and you may want to customize it to your needs
         */
        var result =
            a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
        return result * sortOrder;
    };
}
</script>
